// Product slots and native Shopify pages are separate: a promotion is one cell.
class ReytelCatalogLayout {
  static plan(total, size, page, campaign = false, terrain = false) {
    total = Math.max(0, Math.floor(Number(total) || 0));
    size = Math.max(1, Math.floor(Number(size) || 1));
    page = Math.max(1, Math.floor(Number(page) || 1));
    const ads = total > 0 && size > 1;
    campaign = Boolean(campaign && ads);
    terrain = Boolean(terrain && total>0 && size>2);
    let pages = Math.ceil(total / size), previous;
    do {
      previous = pages;
      pages = Math.ceil((total + (campaign ? 1 : 0) + (ads ? Math.floor(pages / 3) : 0) + (terrain && pages>=3 ? 1:0)) / size);
    } while (pages !== previous);
    const before = (campaign && page > 1 ? 1 : 0) + (ads ? Math.floor((page - 1) / 3) : 0) + (terrain && page>3 ? 1:0);
    const exclusive = ads && page <= pages && page % 3 === 0;
    const promotion = campaign && page === 1;
    const relief = terrain && page===3 && page<=pages;
    const start = Math.max(0, (page - 1) * size - before);
    const count = Math.max(0, Math.min(total - start, size - (exclusive || promotion ? 1 : 0) - (relief?1:0)));
    const nativePages = count ? Array.from({length: Math.floor((start + count - 1) / size) - Math.floor(start / size) + 1}, (_, n) => Math.floor(start / size) + n + 1) : [];
    return {total, size, page, pages, start, count, exclusive, promotion, relief, nativePages};
  }
}

class PaginationController {
	constructor(options = {}) {
		this.paginationSelector = options.paginationSelector || '[data-pagination]';
		this.paginationSectionId = options.paginationSectionId;
		this.contentSectionId = options.contentSectionId;
		this.containerSelector = options.containerSelector;
		this.sourceSelector = options.sourceSelector || this.containerSelector;
		this.append = options.append ?? true;
		this.freezeOnReplace = options.freezeOnReplace ?? true;
		this.getCurrentPage = options.getCurrentPage;
		this.getTotalPages = options.getTotalPages;
		this.onPageChange = options.onPageChange;
		this.isLoading = false;
        this.catalogSourceCache = new Map();
        this.catalogEpoch = 0;

        this.catalogMode = !!this.catalogConfig();
        if (this.catalogMode) document.documentElement.classList.add('rx-catalog-js');
        if (this.catalogMode) window.addEventListener('popstate', () => window.location.reload());

		this.init();
	}

	init() {
		this.bindClicks();
		this.bindInfinite();
		this.updateInfiniteState();
	}

	bindClicks() {
		document.body.addEventListener('click', async (e) => {
			const link = e.target.closest('.page-pagination a[href]');
			if (!link || link.classList.contains('disabled')) return;

			e.preventDefault();

			if (this.isLoading) return;

			const url = new URL(link.href);
			const page = parseInt(url.searchParams.get('page'), 10);
			if (isNaN(page)) return;

			try {
				this.isLoading = true;
				this.freezeLayout();

				await this.onPageChange?.(page, { append: false });
				await this.updatePagination(page);

				window.scrollTo({ top: 0, behavior: "auto" });
				const currentContent = document.querySelector(this.containerSelector);
				window.syncProductCardMedia?.(currentContent);
			} finally {
				this.unfreezeLayout();
				this.isLoading = false;
			}
		});
	}

	bindInfinite() {
		document.addEventListener('infinite:next', async () => {
			if (this.isLoading) return;

			try {
				this.isLoading = true;

				const nextPage = this.getCurrentPage() + 1;

				await this.onPageChange?.(nextPage, { append: true });
				await this.updatePagination(nextPage);

				const currentContent = document.querySelector(this.containerSelector);
				window.syncProductCardMedia?.(currentContent);
			} finally {
				this.isLoading = false;
			}
		});
	}
	freezeLayout() {
		if (!this.freezeOnReplace) return;

		this.container = document.querySelector(this.containerSelector);
		if (!this.container) return;

		this.frozenHeight = this.container.offsetHeight;
		this.container.style.minHeight = this.frozenHeight + 'px';

		this.container.style.opacity = '0.4';
		this.container.style.pointerEvents = 'none';
	}

	unfreezeLayout() {
		if (!this.container) return;

		this.container.style.minHeight = '';
		this.container.style.opacity = '';
		this.container.style.pointerEvents = '';

		this.container = null;
		this.frozenHeight = null;
	}

	scrollToPagination(offset = 150) {
		const paginationBlock = document.querySelector('[data-pagination]');
		if (!paginationBlock) return;

		const rect = paginationBlock.getBoundingClientRect();
		const scrollTarget = window.scrollY + rect.bottom - window.innerHeight + offset;
		window.scrollTo({
			top: scrollTarget,
			behavior: "smooth"
		});
	}

  catalogConfig(grid = document.querySelector(this.containerSelector)) {
    const meta = grid?.querySelector('[data-rx-catalog]');
    if (!meta) return null;
    return {total: Number(meta.dataset.total), size: Number(meta.dataset.size), nativePage: Number(meta.dataset.nativePage), campaign: meta.dataset.campaign === 'true', terrain: meta.dataset.terrain === 'true', empty: meta.dataset.empty};
  }

  catalogBaseUrl() {
    const url = new URL(window.location.href);
    for (const key of ['page', 'section_id', 'sections', '_']) url.searchParams.delete(key);
    url.searchParams.sort();
    return url;
  }

  rememberCatalogSource(grid, base) {
    const config = this.catalogConfig(grid);
    const products = [...grid.querySelectorAll(':scope > .product-cards__card')];
    if (!config || config.nativePage < 1) return;
    const expected = Math.max(0, Math.min(config.size, config.total - (config.nativePage - 1) * config.size));
    if (products.length !== expected) return; // Already assembled grids are never native sources.
    const key = base.toString() + '|' + config.nativePage;
    this.catalogSourceCache.set(key, Promise.resolve({grid: grid.cloneNode(true), products: products.map(p => p.cloneNode(true)), config}));
  }

  async catalogSource(nativePage, base) {
    const key = base.toString() + '|' + nativePage;
    if (!this.catalogSourceCache.has(key)) {
      const url = new URL(base);
      url.searchParams.set('page', nativePage);
      url.searchParams.set('section_id', this.contentSectionId);
      const promise = (async () => {
        const response = await fetch(url.toString());
        if (!response.ok) throw new Error('Catalog section request failed');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const grid = doc.querySelector(this.sourceSelector), config = this.catalogConfig(grid);
        if (!grid || !config || config.nativePage !== nativePage) throw new Error('Invalid catalog section');
        const products = [...grid.querySelectorAll(':scope > .product-cards__card')];
        const expected = Math.max(0, Math.min(config.size, config.total - (nativePage - 1) * config.size));
        if (products.length !== expected) throw new Error('Incomplete catalog source');
        return {grid, products, config};
      })();
      this.catalogSourceCache.set(key, promise);
      promise.catch(() => {if (this.catalogSourceCache.get(key) === promise) this.catalogSourceCache.delete(key);});
      while (this.catalogSourceCache.size > 8) this.catalogSourceCache.delete(this.catalogSourceCache.keys().next().value);
    }
    return this.catalogSourceCache.get(key);
  }

  async renderCatalogPage(page, {append = false, initial = false} = {}) {
    const current = document.querySelector(this.containerSelector), config = this.catalogConfig(current);
    if (!current || !config) return false;
    const base = this.catalogBaseUrl(), epoch = ++this.catalogEpoch;
    const plan = ReytelCatalogLayout.plan(config.total, config.size, page, config.campaign, config.terrain);
    if (page > plan.pages && plan.pages > 0) throw new Error('Catalog page is out of range');
    if (initial && current.dataset.rxAssembled !== 'true') this.rememberCatalogSource(current, base);
    const sources = await Promise.all(plan.nativePages.map(n => this.catalogSource(n, base)));
    if (epoch !== this.catalogEpoch || this.catalogBaseUrl().toString() !== base.toString()) return false;
    if (sources.some(s => s.config.total !== config.total || s.config.size !== config.size)) throw new Error('Catalog changed during pagination');
    const byIndex = new Map();
    for (const source of sources) source.products.forEach((p, i) => byIndex.set((source.config.nativePage - 1) * config.size + i, p));
    const fragment = document.createDocumentFragment();
    if (plan.promotion) fragment.append(current.querySelector('[data-rx-campaign]').content.cloneNode(true));
    if (plan.exclusive) fragment.append(current.querySelector('[data-rx-exclusive]').content.cloneNode(true));
    for (let i = plan.start; i < plan.start + plan.count; i++) {
      if (!byIndex.has(i)) throw new Error('Missing catalog product');
      const product = byIndex.get(i).cloneNode(true);
      product.classList.remove('catalog-product--carry', 'rx-native-product');
      product.dataset.catalogIndex = String(i);
      product.dataset.catalogPage = String(page);
      fragment.append(product);
      if(plan.relief && i===plan.start+2)fragment.append(current.querySelector('[data-rx-terrain]').content.cloneNode(true));
    }
    if(plan.relief && plan.count<3)fragment.append(current.querySelector('[data-rx-terrain]').content.cloneNode(true));
    if (!plan.total) { const empty = document.createElement('p'); empty.textContent = config.empty; fragment.append(empty); }
    if (!append) {
      const templates = [...current.querySelectorAll(':scope > template')].map(t => t.cloneNode(true));
      window.destroyProductCardMedia?.(current);
      current.replaceChildren(...templates, fragment);
    } else current.append(fragment);
    current.dataset.rxReady = 'true';
    current.dataset.rxAssembled = 'true';
    current.dataset.rxPage = String(page);
    this.catalogPlan = plan;
    window.syncProductCardMedia?.(current);
    return true;
  }

  renderCatalogPagination(page) {
    const grid = document.querySelector(this.containerSelector), config = this.catalogConfig(grid);
    if (!config) return;
    const plan = ReytelCatalogLayout.plan(config.total, config.size, page, config.campaign, config.terrain);
    const root = document.querySelector(this.paginationSelector);
    if (!root) return;
    const nav = document.createElement('div'); nav.className = 'page-pagination';
    nav.dataset.currentPage = String(page); nav.dataset.totalPages = String(plan.pages);
    const template = grid.querySelector('[data-rx-pagination]');
    const href = number => {const u = this.catalogBaseUrl();u.searchParams.set('page', number);return u.toString();};
    const arrow = (kind, number, disabled) => {
      const a = template.content.querySelector('.' + kind).cloneNode(true);
      if (disabled) { a.classList.add('disabled'); a.setAttribute('aria-disabled', 'true'); }
      else a.href = href(number);
      return a;
    };
    nav.append(arrow('prev', page - 1, page <= 1));
    let previous = 0;
    for (let n = 1; n <= plan.pages; n++) {
      if (n !== 1 && n !== plan.pages && Math.abs(n - page) > 2) continue;
      if (previous && n - previous > 1) {const dots = document.createElement('span');dots.className = 'dots';dots.textContent = '…';nav.append(dots);}
      const wrap = document.createElement('div');wrap.className = 'page-pagination__wrap';
      const link = document.createElement('a');link.className = 'page';link.dataset.page = String(n);link.textContent = String(n);
      if (n === page) {link.classList.add('active');link.setAttribute('aria-current', 'page');link.setAttribute('aria-disabled', 'true');}
      else link.href = href(n);
      wrap.append(link);nav.append(wrap);previous = n;
    }
    nav.append(arrow('next', page + 1, page >= plan.pages));
    root.replaceChildren(nav);root.style.display = plan.pages > 1 ? '' : 'none';
    this.updateInfiniteState();
  }

  catalogCommitUrl(page, append) {
    const url = this.catalogBaseUrl();
    if (page > 1) url.searchParams.set('page', page);
    if (append) window.history.replaceState({}, '', url);
    else if (url.toString() !== window.location.href) window.history.pushState({}, '', url);
  }


	activateCarryProducts(productCards) {
		productCards?.forEach((productCard) => {
			productCard.classList.remove('catalog-product--carry');
			productCard.dataset.catalogCarried = 'true';
		});
	}

	insertCarryProducts(content, productCards) {
		if (!content || !productCards?.length) return;

		const banners = content.querySelectorAll(':scope > .catalog-banner-card');
		const lastBanner = banners[banners.length - 1];

		if (lastBanner) {
			lastBanner.after(...productCards);
		} else {
			content.prepend(...productCards);
		}
	}

	async fetchCarryProducts(page) {
		if (page < 1) return [];

		const url = new URL(window.location.href);
		url.searchParams.set('page', page);
		url.searchParams.set('section_id', this.contentSectionId);

		const res = await fetch(url.toString());
		const html = await res.text();
		const doc = new DOMParser().parseFromString(html, 'text/html');
		return [...doc.querySelectorAll(`${this.sourceSelector} .catalog-product--carry`)];
	}

	async normalizeInitialPage(page) {
        if (this.catalogMode) {
            try {
                await this.renderCatalogPage(page, {initial: true});
                this.renderCatalogPagination(page);
            } catch (error) {
                const grid = document.querySelector(this.containerSelector);
                if (grid) grid.dataset.rxReady = 'true'; // Native Shopify products remain usable on a network failure.
                console.warn('[REYTEL catalog] Native pagination fallback', error);
            }
            return;
        }
		if (page <= 1) return;

		const currentContent = document.querySelector(this.containerSelector);
		if (!currentContent || currentContent.querySelector('[data-catalog-carried]')) return;

		const carryProducts = await this.fetchCarryProducts(page - 1);
		if (!carryProducts.length) return;

		this.activateCarryProducts(carryProducts);
		this.insertCarryProducts(currentContent, carryProducts);
		window.syncProductCardMedia?.(currentContent);
	}

	async loadPage(page, { append = false } = {}) {
        if (this.catalogMode) {
            const grid = document.querySelector(this.containerSelector);
            if (append && grid?.dataset.rxAssembled !== 'true') {
                const restored = await this.renderCatalogPage(this.getCurrentPage(), {initial: true});
                if (!restored) return false;
            }
            const rendered = await this.renderCatalogPage(page, {append});
            if (rendered) this.catalogCommitUrl(page, append);
            return rendered;
        }
		const url = new URL(window.location.href);
		url.searchParams.set('page', page);

		const res = await fetch(
			`${url}&section_id=${this.contentSectionId}`
		);
		const html = await res.text();

		const doc = new DOMParser().parseFromString(html, 'text/html');

		const newContent = doc.querySelector(this.sourceSelector);
		const currentContent = document.querySelector(this.containerSelector);

		if (!newContent || !currentContent) return;

		if (append) {
			const carryProducts = [...currentContent.querySelectorAll('.catalog-product--carry')];
			this.activateCarryProducts(carryProducts);
			if (newContent.querySelector(':scope > .catalog-banner-card')) {
				this.insertCarryProducts(newContent, carryProducts);
			}
			while (newContent.firstChild) {
				currentContent.appendChild(newContent.firstChild);
			}
		} else {
			if (page > 1) {
				const carryProducts = await this.fetchCarryProducts(page - 1);
				if (carryProducts.length) {
					this.activateCarryProducts(carryProducts);
					this.insertCarryProducts(newContent, carryProducts);
				}
			}
			window.destroyProductCardMedia?.(currentContent);
			currentContent.innerHTML = newContent.innerHTML;
		}

		window.syncProductCardMedia?.(currentContent);
	}

	async loadPageBlogs(page, { append = false } = {}) {
		const wrapper = document.querySelector('[data-blog-search-wrapper]');
		const baseUrl = wrapper?.dataset.blogUrl;

		if (!baseUrl) {
			console.warn('[PaginationController] blogUrl not found');
			return;
		}

		const url = new URL(baseUrl, window.location.origin);
		url.searchParams.set('page', page);

		const res = await fetch(
			`${url}&section_id=${this.contentSectionId}`
		);

		const html = await res.text();
		const doc = new DOMParser().parseFromString(html, 'text/html');

		const newContent = doc.querySelector(this.sourceSelector);
		const currentContent = document.querySelector(this.containerSelector);

		if (!newContent || !currentContent) return;
		if (append) {
			currentContent.insertAdjacentHTML(
				'beforeend',
				newContent.innerHTML
			);
		} else {
			window.destroyProductCardMedia?.(currentContent);
			currentContent.innerHTML = newContent.innerHTML;
		}

		window.syncProductCardMedia?.(currentContent);
	}

	async updatePagination(page) {
        if (this.catalogMode) {this.renderCatalogPagination(this.getCurrentPage?.() ?? page);return;}
		const pagination = document.querySelector(this.paginationSelector);
		if (!pagination) return;

		const url = new URL(window.location.href);
		url.searchParams.set('page', page);

		const res = await fetch(`${url}&section_id=${this.paginationSectionId}`);
		const html = await res.text();

		const doc = new DOMParser().parseFromString(html, 'text/html');
		const newPagination = doc.querySelector(this.paginationSelector);

		if (newPagination) {
			pagination.replaceWith(newPagination);
		}

		this.syncPrevNext();
		this.syncActive();
		this.updateInfiniteState();

		const total = this.getTotalPages?.() ?? 1;

		const paginationRoot = document.querySelector(this.paginationSelector);
		if (paginationRoot) {
			paginationRoot.style.display = total > 1 ? '' : 'none';
		}
	}

	syncActive() {
		const currentPage = this.getCurrentPage();

		document.querySelectorAll('.page-pagination__wrap a').forEach(a => {
			const page = parseInt(a.textContent.trim());
			if (isNaN(page)) return;

			a.classList.toggle('active', page === currentPage);
			a.toggleAttribute('aria-current', page === currentPage);
		});
	}

	syncPrevNext() {
		const current = this.getCurrentPage();
		const total = this.getTotalPages();

		const prev = document.querySelector('.prev');
		const next = document.querySelector('.next');

		if (prev) {
			prev.classList.toggle('disabled', current <= 1);
		}

		if (next) {
			next.classList.toggle('disabled', current >= total);
		}
	}

	updateInfiniteState() {
		const infinite = document.querySelector('infinite-pagination');
		if (!infinite) return;

		infinite.dataset.currentPage = this.getCurrentPage?.() ?? 1;
		infinite.dataset.totalPages = this.getTotalPages?.() ?? 1;

		infinite.updateState();
	}
}

// Infinite pagination

customElements.define('infinite-pagination', class InfinitePagination extends HTMLElement {
		connectedCallback() {
			this.button = this.querySelector('a');
			if (!this.button) return;

			this.button.addEventListener('click', this.onClick.bind(this));
			this.updateState();
		}

		updateState() {
			const current = Number(this.dataset.currentPage || 1);
			const total = Number(this.dataset.totalPages || 1);

			if (total <= 1 || current >= total) {
				this.classList.add('hidden');
			} else {
				this.classList.remove('hidden');
			}
		}

		onClick(e) {
			e.preventDefault();
			this.dispatchEvent(
				new CustomEvent('infinite:next', { bubbles: true })
			);
		}
	}
);